<script setup>
import CtaBanner from '@/views/front-pages/landing-page/CtaBanner.vue'
import PageHero from '@/views/front-pages/PageHero.vue'
import PublicPageShell from '@/views/front-pages/PublicPageShell.vue'
import { useApi } from '@/utils/api'
import { useConfigStore } from '@core/stores/config'

definePage({ meta: { layout: 'blank', public: true } })

const store = useConfigStore()

store.skin = 'default'

// Addendum 15, Modul BT §6 poin 3: ilustrasi/hero OPSIONAL, diupload admin
// lewat /platform > SEO Halaman Publik (reuse og_image_path, lihat
// SeoSettingService::heroImageUrl()) -- kalau belum diupload, heroImageUrl
// tetap null dan halaman ini tampil PERSIS seperti sebelum modul ini ada
// (tanpa gambar), tidak ada regresi.
const heroImageUrl = ref(null)

const fetchHeroImage = async () => {
  try {
    const response = await useApi('/api/public/page-hero-image', { query: { path: '/tentang-kami' } }).get()
    const body = response?.data ?? response

    heroImageUrl.value = body?.image_url ?? null
  } catch (err) {
    console.error('❌ Gagal memuat gambar ilustrasi halaman:', err)
  }
}

onMounted(fetchHeroImage)

const values = [
  {
    icon: 'ri-focus-3-line',
    color: 'primary',
    title: 'Fokus UMKM & Coffee Shop',
    desc: 'Setiap fitur dirancang dari kebutuhan nyata operasional coffee shop dan UMKM F&B Indonesia, bukan dipaksakan dari template POS generik.',
  },
  {
    icon: 'ri-price-tag-3-line',
    color: 'success',
    title: 'Harga Transparan',
    desc: 'Tanpa biaya tersembunyi. Harga yang tertulis adalah harga yang dibayar, sudah termasuk hosting, SSL, dan backup harian.',
  },
  {
    icon: 'ri-shield-check-line',
    color: 'info',
    title: 'Data Aman & Terisolasi',
    desc: 'Setiap tenant terisolasi penuh di level database, dengan audit log untuk setiap perubahan data penting.',
  },
  {
    icon: 'ri-customer-service-2-line',
    color: 'warning',
    title: 'Support Berbahasa Indonesia',
    desc: 'Tim support yang mengerti konteks bisnis F&B lokal, siap membantu lewat WhatsApp dan email.',
  },
]
</script>

<template>
  <PublicPageShell>
    <PageHero
      eyebrow="Tentang Kami"
      eyebrow-icon="ri-store-3-line"
      title="Kami Membangun PintuKasir untuk Bisnis Seperti Anda"
      subtitle="PintuKasir hadir untuk membantu pemilik coffee shop dan UMKM F&B mengelola kasir, stok, dan keuangan tanpa perlu tim IT sendiri."
    />

    <section v-if="heroImageUrl" class="about-hero-image-section pb-10">
      <VContainer>
        <VImg
          :src="heroImageUrl"
          rounded="xl"
          cover
          max-height="420"
          class="mx-auto about-hero-image"
        />
      </VContainer>
    </section>

    <section class="about-story-section pb-16">
      <VContainer>
        <VRow justify="center">
          <VCol
            cols="12"
            md="9"
            lg="8"
          >
            <VCard
              rounded="xl"
              variant="outlined"
              class="pa-2 pa-md-4"
            >
              <VCardText class="pa-6 pa-md-8">
                <h2 class="text-h5 font-weight-bold mb-4">
                  Kenapa PintuKasir?
                </h2>
                <p
                  class="text-body-1 text-medium-emphasis mb-4"
                  style="line-height: 1.8"
                >
                  Banyak coffee shop dan UMKM F&B di Indonesia masih mengelola kasir, stok bahan baku, dan
                  keuangan secara manual atau lewat aplikasi yang tidak dirancang khusus untuk kebutuhan
                  mereka. Akibatnya, pemilik bisnis kehilangan waktu untuk hal-hal administratif, padahal
                  waktu itu lebih berharga untuk memikirkan rasa, layanan, dan pertumbuhan usaha.
                </p>
                <p
                  class="text-body-1 text-medium-emphasis mb-0"
                  style="line-height: 1.8"
                >
                  PintuKasir dibangun sebagai platform kasir (POS) yang menyatukan operasional kasir,
                  manajemen resep & stok bahan baku, laporan keuangan, hingga fitur F&B modern seperti
                  self-order QR dan Kitchen Display System -- semua dalam satu aplikasi, dengan harga yang
                  masuk akal untuk skala UMKM.
                </p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VContainer>
    </section>

    <section class="about-values-section pb-16">
      <VContainer>
        <div class="text-center mb-10">
          <h2 class="text-h5 font-weight-bold mb-2">
            Yang Kami Pegang Teguh
          </h2>
          <p class="text-medium-emphasis">
            Prinsip yang jadi pegangan dalam mengembangkan PintuKasir.
          </p>
        </div>

        <VRow>
          <VCol
            v-for="value in values"
            :key="value.title"
            cols="12"
            sm="6"
            md="3"
          >
            <VCard
              class="value-card h-100"
              rounded="lg"
              variant="outlined"
            >
              <VCardText class="pa-6 text-center">
                <div
                  class="value-icon-wrap mx-auto mb-4"
                  :class="`value-icon-wrap--${value.color}`"
                >
                  <VIcon
                    :icon="value.icon"
                    size="26"
                    :color="value.color"
                  />
                </div>
                <h3 class="text-body-1 font-weight-bold mb-2">
                  {{ value.title }}
                </h3>
                <p
                  class="text-body-2 text-medium-emphasis mb-0"
                  style="line-height: 1.6"
                >
                  {{ value.desc }}
                </p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VContainer>
    </section>

    <CtaBanner />
  </PublicPageShell>
</template>

<style lang="scss" scoped>
.about-hero-image {
  max-inline-size: 960px;
}

.value-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(var(--v-shadow-key-umbra-color), 12%) !important;
    transform: translateY(-2px);
  }
}

.value-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;

  &--primary { background: rgba(var(--v-theme-primary), 0.12); }
  &--success { background: rgba(var(--v-theme-success), 0.12); }
  &--info { background: rgba(var(--v-theme-info), 0.12); }
  &--warning { background: rgba(var(--v-theme-warning), 0.12); }
}
</style>
